import {IssueMarkdownViewer} from '@github-ui/commenting/IssueMarkdownViewer'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'
import {ReactionViewer} from '@github-ui/reaction-viewer/ReactionViewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {Box} from '@primer/react'
import {type RefObject, useCallback} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {commitUpdateIssueBodyMutation} from '../mutations/update-issue-body-mutation'
import type {IssueBodyViewer$key} from './__generated__/IssueBodyViewer.graphql'
import type {IssueBodyViewerReactable$key} from './__generated__/IssueBodyViewerReactable.graphql'
import {AddTasklistButton} from './AddTasklistButton'

export type IssueBodyViewerProps = {
  html: SafeHTMLString
  markdown: string
  comment: IssueBodyViewer$key
  onLinkClick?: (event: MouseEvent) => void
  viewerRef?: RefObject<HTMLDivElement>
  issueBodyRef?: React.RefObject<HTMLDivElement>
  bodyVersion: string
  viewerCanUpdate: boolean
  locked: boolean
  reactable: IssueBodyViewerReactable$key
}

export function IssueBodyViewer({
  html,
  markdown,
  comment,
  onLinkClick,
  viewerRef,
  issueBodyRef,
  bodyVersion,
  locked,
  viewerCanUpdate,
  reactable,
}: IssueBodyViewerProps) {
  const {tasklist_block} = useFeatureFlags()
  const environment = useRelayEnvironment()
  const data = useFragment(
    graphql`
      fragment IssueBodyViewer on Comment {
        id
      }
    `,
    comment,
  )
  const dataReaction = useFragment(
    graphql`
      fragment IssueBodyViewerReactable on Reactable {
        ...ReactionViewerGroups
      }
    `,
    reactable,
  )

  const onSave = useCallback(
    (newBody: string, onCompleted: () => void, onError: () => void) => {
      commitUpdateIssueBodyMutation({
        environment,
        input: {issueId: data.id, body: newBody, bodyVersion},
        onCompleted,
        onError,
      })
    },
    [bodyVersion, environment, data.id],
  )
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 4, p: 3}} ref={issueBodyRef}>
      <IssueMarkdownViewer
        html={html}
        markdown={markdown}
        viewerCanUpdate={viewerCanUpdate}
        onSave={onSave}
        onLinkClick={onLinkClick}
      />
      <Box sx={{flexDirection: 'row', display: 'flex', alignItems: 'left'}}>
        {tasklist_block && viewerRef && viewerCanUpdate && <AddTasklistButton bodyRef={viewerRef} />}
        <ReactionViewer subjectId={data.id} reactionGroups={dataReaction} locked={locked} />
      </Box>
    </Box>
  )
}

try{ IssueBodyViewer.displayName ||= 'IssueBodyViewer' } catch {}